import moment from 'moment';
import { VIEW_CHANGE_REFRESH_TIME, VIEW_CHANGE_SELECTION, VIEW_CHANGE_TIME_PERIOD, VIEW_CHANGE_TIME_RANGE, VIEW_DESELECT_PANE, VIEW_SELECT_PANE, VIEW_SET_FILTER, VIEW_SET_PIVOT_PROPERTY, VIEW_UPDATE_CURRENT_TIME } from '@/store/types/actions';
import { VIEW_CHANGE_MODEL, VIEW_CURRENT_TIME_UPDATE, VIEW_DATA_PERIOD_CHANGED, VIEW_FILTER_CHANGED, VIEW_INITIALISED, VIEW_KPI_LEGEND_TOGGLED, VIEW_KPI_SELECTED, VIEW_PANE_DESELECTED, VIEW_PANE_SELECTED, VIEW_PIVOT_PROPERTY_CHANGED, VIEW_REFRESH_TIME_CHANGED, VIEW_SELECTED_DEVICES_CHANGED, VIEW_SHOW_MAX_VALUES_CHANGED, VIEW_SHOW_ZERO_VALUES_CHANGED, VIEW_TIME_PERIOD_CHANGED, VIEW_TIME_RANGE_CHANGED, VIEW_UPDATE_NOW_TIME } from '@/store/types/mutations';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

// getters
const getters = {
  viewInitialised: (state) => !!state.currentPanes.length,
  currentFilterId: (state) => state.currentFilterId,
  viewPivotProperty: (state) => state.viewPivotProperty,
  viewModel: (state) => state.viewModel,
  viewFilter: (state) => state.viewFilter,
  viewPanes: (state) => state.currentPanes,
  currentDevices: (state) => state.currentDevices,
  selectedDevices: (state) => state.selectedIds.map(($id) => ({ $id })),
  currentTimePeriod: (state) => state.currentTimePeriod,
  dataPeriod: (state) => state.dataPeriod,
  viewSelectedKpi: (state) => state.selectedKpi,
  viewKpi: (state) => state.fullKpi,
  viewKpiLegend: (state) => state.kpiLegend,
  viewShowZeroValues: (state) => state.showZeroValues,
  viewShowMaxValues: (state) => state.showMaxValues,
  viewStartTime: (state) => state.dataPeriod.start,
  viewEndTime: (state) => state.dataPeriod.end,
  viewIsNow: (state) => {
    const { timeRange } = state;
    if (timeRange && timeRange.anchor) {
      return moment().isSame(timeRange.anchor, 'day');
    }
    return false;
  },
  viewKpiOffset: (state, get) => {
    if (!state.dataPeriod.end) return 0;
    if (get.viewIsNow) {
      return 0;
    }
    return moment().valueOf() - state.dataPeriod.end.valueOf();
  },
  viewKpiPeriod: (state) => {
    if (!state.dataPeriod.start || !state.dataPeriod.end) return 0;
    return state.dataPeriod.end.valueOf() - state.dataPeriod.start.valueOf();
  },
  viewTimeRange: (state) => state.timeRange,
  viewLastRefresh: (state) => state.lastRefresh,
};

// actions
const actions = {
  [VIEW_SET_PIVOT_PROPERTY]({ commit }, property) {
    commit(VIEW_PIVOT_PROPERTY_CHANGED, { property });
  },
  [VIEW_SET_FILTER]({ commit }, filter) {
    commit(VIEW_FILTER_CHANGED, { filter });
  },
  [VIEW_SELECT_PANE]({ commit, state }, paneType) {
    commit(VIEW_INITIALISED);
    // Deselect if it has already been selected
    if (state.currentPanes.indexOf(paneType) > -1) {
      commit(VIEW_PANE_DESELECTED, paneType);
    } else {
      commit(VIEW_PANE_SELECTED, paneType);
    }
  },
  [VIEW_DESELECT_PANE]({ commit }, paneType) {
    commit(VIEW_PANE_DESELECTED, paneType);
  },
  [VIEW_CHANGE_SELECTION]({ commit }, idArray) {
    commit(VIEW_SELECTED_DEVICES_CHANGED, idArray);
  },
  [VIEW_CHANGE_TIME_PERIOD]({ commit }, timePeriod) {
    commit(VIEW_TIME_PERIOD_CHANGED, timePeriod);
    commit(VIEW_CURRENT_TIME_UPDATE);
  },
  [VIEW_CHANGE_TIME_RANGE]({ commit }, timeRange) {
    commit(VIEW_TIME_RANGE_CHANGED, timeRange);
    commit(VIEW_CURRENT_TIME_UPDATE);
  },
  [VIEW_UPDATE_CURRENT_TIME]({ commit }) {
    commit(VIEW_CURRENT_TIME_UPDATE);
  },
  [VIEW_CHANGE_REFRESH_TIME]({ commit }, time) {
    commit(VIEW_CURRENT_TIME_UPDATE);
    commit(VIEW_UPDATE_NOW_TIME);
    commit(VIEW_REFRESH_TIME_CHANGED, time);
  },
};

// mutations
const mutations = {
  [VIEW_PIVOT_PROPERTY_CHANGED](state, { property }) {
    state.viewPivotProperty = property;
    state.selectedIds = [];
  },
  [VIEW_FILTER_CHANGED](state, { filter }) {
    state.viewFilter = filter;
    state.selectedIds = [];
  },
  [VIEW_PANE_SELECTED](state, paneType) {
    if (state.currentPanes.indexOf(paneType) > -1) {
      return;
    }
    state.currentPanes.push(paneType);
  },
  [VIEW_PANE_DESELECTED](state, paneType) {
    state.currentPanes = state.currentPanes.filter((p) => p !== paneType);
  },
  [VIEW_SELECTED_DEVICES_CHANGED](state, deviceIdArray) {
    state.selectedIds = deviceIdArray;
  },
  [VIEW_TIME_PERIOD_CHANGED](state, timePeriod) {
    state.currentTimePeriod = timePeriod;
  },
  [VIEW_TIME_RANGE_CHANGED](state, timeRange) {
    state.timeRange = timeRange;
  },
  [VIEW_CURRENT_TIME_UPDATE](state) {
    state.now = Date.now();
  },
  [VIEW_INITIALISED](state) {
    state.initialised = true;
  },
  [VIEW_REFRESH_TIME_CHANGED](state, time) {
    state.lastRefresh = time;
  },
  [VIEW_KPI_SELECTED](state, { fullKpi, parsedKpi }) {
    state.fullKpi = fullKpi;
    state.selectedKpi = parsedKpi;
  },
  [VIEW_KPI_LEGEND_TOGGLED](state, colour) {
    state.kpiLegend[colour] = !state.kpiLegend[colour];
  },
  [VIEW_SHOW_ZERO_VALUES_CHANGED](state, newVal) {
    state.showZeroValues = newVal;
  },
  [VIEW_SHOW_MAX_VALUES_CHANGED](state, newVal) {
    state.showMaxValues = newVal;
  },
  [VIEW_DATA_PERIOD_CHANGED](state, dataPeriod) {
    state.dataPeriod = dataPeriod;
  },
  [VIEW_UPDATE_NOW_TIME](state) {
    const { period } = state.dataPeriod;
    const end = moment().startOf('minute');
    const start = moment(end);
    start.subtract(period, 'milliseconds');

    if (this.timePeriod >= DAY) {
      start.add(1, 'milliseconds');
    }

    state.dataPeriod = {
      start,
      end,
      period,
    };
  },
  [VIEW_CHANGE_MODEL](state, model) {
    state.viewModel = model;
  },
};

// initial state
const state = {
  viewPivotProperty: null,
  viewFilter: null,
  viewModel: null,
  currentPanes: [],
  currentDevices: {},
  selectedIds: [],
  currentTimePeriod: null,
  timeRange: null,
  dataPeriod: { start: null, end: null, period: null },
  initialised: false,
  selectedKpi: null,
  fullKpi: null,
  kpiLegend: { green: true, amber: true, red: true },
  showZeroValues: true,
  showMaxValues: true,
  lastRefresh: new Date(),
  now: Date.now(), // This is to create a reactive date
};

export default {
  state,
  getters,
  actions,
  mutations,
};
